import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import apiService from '../api';
import '../styles/pages/Products.scss';

// Imagens para uso offline (fallback)
import pijama1 from '../assets/images/pijama1.jpg';
import pijama2 from '../assets/images/pijama2.jpg';

const Products = () => {
  const [products, setProducts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [networkStatus, setNetworkStatus] = useState('checking');
  const [source, setSource] = useState(''); // Para rastrear a origem dos dados

  // Lista de imagens de fallback para uso offline
  const fallbackImages = [pijama1, pijama2];

  // Produtos de demonstração para garantir que algo seja exibido
  const demoProducts = [
    {
      id: 1, 
      name: 'Pijama de Malha Fria', 
      price: 38.90, 
      images: [{ url: pijama1 }]
    },
    {
      id: 2, 
      name: 'Pijama de Malha Fria', 
      price: 38.90, 
      images: [{ url: pijama2 }]
    }
  ];

  // Função para obter produtos do localStorage adicionados pelo admin
  const getLocalStorageProducts = () => {
    try {
      const storedProducts = localStorage.getItem('lunaProducts');
      if (storedProducts) {
        const parsedProducts = JSON.parse(storedProducts);
        console.log("Produtos encontrados no localStorage:", parsedProducts.length);
        return parsedProducts;
      }
    } catch (error) {
      console.error("Erro ao buscar produtos do localStorage:", error);
    }
    return null;
  };

  // Primeiro, verificamos a conexão com a API
  useEffect(() => {
    console.log("Verificando status da API...");
    const checkApiStatus = async () => {
      try {
        await apiService.testConnection();
        setNetworkStatus('online');
        console.log("API está online");
      } catch (error) {
        console.error("API está offline:", error);
        setNetworkStatus('offline');
        
        // Se a API estiver offline, verificamos o localStorage
        const localProducts = getLocalStorageProducts();
        if (localProducts && localProducts.length > 0) {
          console.log("Usando produtos do localStorage");
          setProducts(localProducts);
          setSource('localStorage');
          setLoading(false);
        } else {
          // Se não houver produtos no localStorage, usamos os de demonstração
          console.log("Usando produtos de demonstração");
          setProducts(demoProducts);
          setSource('demo');
          setLoading(false);
        }
      }
    };
    
    checkApiStatus();
  }, []);

  // Em seguida, buscamos os produtos se a API estiver online
  useEffect(() => {
    console.log("Estado atual da conexão:", networkStatus);
    
    if (networkStatus === 'online') {
      console.log("Iniciando busca de produtos da API...");
      
      const fetchProducts = async () => {
        try {
          const data = await apiService.getProducts();
          console.log("Produtos recebidos da API:", data);
          
          if (data && data.length > 0) {
            setProducts(data);
            setSource('api');
            setLoading(false);
            setError(null);
          } else {
            console.warn("API retornou uma lista vazia de produtos");
            
            // Verificar se temos produtos no localStorage
            const localProducts = getLocalStorageProducts();
            if (localProducts && localProducts.length > 0) {
              console.log("API vazia, usando produtos do localStorage");
              setProducts(localProducts);
              setSource('localStorage');
              setLoading(false);
            } else {
              // Se não houver produtos no localStorage, usamos os de demonstração
              setProducts(demoProducts);
              setSource('demo');
              setLoading(false);
            }
          }
        } catch (err) {
          console.error('Erro ao buscar produtos:', err);
          
          // Verificar se temos produtos no localStorage
          const localProducts = getLocalStorageProducts();
          if (localProducts && localProducts.length > 0) {
            console.log("Erro na API, usando produtos do localStorage");
            setProducts(localProducts);
            setSource('localStorage');
            setLoading(false);
          } else {
            // Se não houver produtos no localStorage, usamos os de demonstração
            setProducts(demoProducts);
            setSource('demo');
            setLoading(false);
          }
        }
      };

      // Tentar buscar produtos da API, com um tempo limite para fallback
      const timeoutId = setTimeout(() => {
        if (loading) {
          console.log("Timeout atingido. Verificando localStorage...");
          
          // Verificar se temos produtos no localStorage
          const localProducts = getLocalStorageProducts();
          if (localProducts && localProducts.length > 0) {
            console.log("Timeout na API, usando produtos do localStorage");
            setProducts(localProducts);
            setSource('localStorage');
            setLoading(false);
          } else {
            // Se não houver produtos no localStorage, usamos os de demonstração
            setProducts(demoProducts);
            setSource('demo');
            setLoading(false);
          }
        }
      }, 8000); // 8 segundos de timeout

      fetchProducts();

      return () => clearTimeout(timeoutId);
    }
  }, [networkStatus, loading]);

  // Variantes de animação
  const containerVariants = {
    hidden: { opacity: 0 },
    visible: { 
      opacity: 1,
      transition: { 
        staggerChildren: 0.1 
      }
    }
  };

  const itemVariants = {
    hidden: { opacity: 0, y: 20 },
    visible: { opacity: 1, y: 0 }
  };

  // Debug render
  console.log("Renderizando - status da rede:", networkStatus, "loading:", loading, "error:", error, "produtos:", products.length, "fonte:", source);

  if (loading && networkStatus !== 'offline') {
    return (
      <motion.div 
        className="products-page loading"
        initial={{ opacity: 0 }}
        animate={{ opacity: 1 }}
        exit={{ opacity: 0 }}
      >
        <h2>Carregando produtos...</h2>
        <p>Aguarde enquanto buscamos os produtos</p>
      </motion.div>
    );
  }

  return (
    <motion.div 
      className="products-page"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <div className="container">
        <h1>Nossos Produtos</h1>
        
        <motion.div 
          className="products-grid"
          variants={containerVariants}
          initial="hidden"
          animate="visible"
        >
          {products && products.length > 0 ? (
            products.map((product, index) => (
              <motion.div 
                key={product.id || index} 
                className="product-card"
                variants={itemVariants}
              >
                <Link to={`/produtos/${product.id || index}`}>
                  <div className="product-image">
                    <img 
                      src={
                        // Verificar se há imagens disponíveis
                        product.images && product.images.length > 0 
                          ? (product.images[0].url || product.images[0].image_url) // Tentar url ou image_url
                          : fallbackImages[index % fallbackImages.length] // Usar imagem de fallback
                      } 
                      alt={product.name} 
                      onError={(e) => {
                        console.log("Erro ao carregar imagem, usando fallback");
                        e.target.src = fallbackImages[index % fallbackImages.length];
                      }}
                    />
                  </div>
                  <div className="product-info">
                    <h3>{product.name}</h3>
                    <p className="product-price">R$ {typeof product.price === 'number' ? product.price.toFixed(2) : product.price}</p>
                  </div>
                </Link>
              </motion.div>
            ))
          ) : (
            <p className="no-products">Nenhum produto disponível no momento</p>
          )}
        </motion.div>
      </div>
    </motion.div>
  );
};

export default Products; 